import React, { useState, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { useConversation } from 'src/pages/main/dmTalk/hooks/useConversation'
import LoadingIcon from 'src/components/layouts/LoadingIcon'
import { ItemChatReceived } from 'src/components/message/ChatBox/MessageReceived'
import { ItemChatRealTalk } from 'src/pages/main/storyTalk/components/ItemChatRealTalk'
import useChatRoom from 'src/services/common/useChatRoom'
import { saveMessage } from 'src/services/patch/saveMessage'
import { authActionTypes, useAuthDispatch, useAuthState } from 'src/providers/AuthProviders'
import { setDisplayFooter } from 'src/redux/layout/setDisplayLayout'
import { CHARACTER_NAVIGATOR_ID } from 'src/constants/characters'
import NoContent from 'src/components/layouts/NoContent'
import { MAIN_PATHS } from 'src/routes/main'
import { useLoadingMore } from 'src/services/hooks/useLoadingMore'

export default function MessageNavigator({ setUnreadNavigator }) {
  const { user } = useAuthState()
  const lastScrollHeight = useRef(0)
  const authDispatch = useAuthDispatch()
  const dispatch = useDispatch()
  const [newMessage, setNewMessage] = useState('')
  const inputRef = useRef(null)
  const roomChatRef = useRef(null)
  const isInitialLoad = useRef(true)

  const {
    data: conversationData,
    isFetching,
    addNewMessageToCache,
    isFetched,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status
  } = useConversation('realtime_chat')
  const firstPage = conversationData?.pages?.find((page) => page?.first_page)
  const hasFirstPage = !(
    conversationData &&
    conversationData.pages.length &&
    conversationData.pages[0] === undefined
  )
  const { message, sendMessage } = useChatRoom(firstPage?.conversation_id)
  const isNoContent = useMemo(
    () => isFetched && hasFirstPage && !firstPage?.character?.navigator,
    [firstPage, isFetched]
  )
  const allMessages = conversationData?.pages?.flatMap((page) => page?.messages || []) || []

  useEffect(() => {
    refetch()
    setUnreadNavigator(false)
    dispatch(setDisplayFooter())
  }, [])

  useEffect(() => {
    if (roomChatRef.current && status === 'success' && !isFetchingNextPage) {
      const newScrollHeight = roomChatRef.current.scrollHeight

      if (isInitialLoad.current) {
        roomChatRef.current.scrollTop = newScrollHeight
        lastScrollHeight.current = newScrollHeight
        isInitialLoad.current = false
      }
    }
  }, [status, isFetchingNextPage])

  useEffect(() => {
    if (!isFetched || !roomChatRef.current) return

    roomChatRef.current.scrollTop = roomChatRef.current.scrollHeight
  }, [isFetched])

  useEffect(() => {
    if (!message) return
    addNewMessageToCache(message)

    setTimeout(() => {
      roomChatRef.current?.scrollTo({
        top: roomChatRef.current.scrollHeight
      })
    }, 100)
  }, [message])

  useLoadingMore({
    selector: '#start',
    skip: !hasNextPage || isFetchingNextPage,
    onLoadMore: async () => {
      await fetchNextPage()
      requestAnimationFrame(() => {
        if (roomChatRef.current) {
          const newScrollHeight = roomChatRef.current.scrollHeight
          if (lastScrollHeight.current > 0) {
            roomChatRef.current.scrollTop = newScrollHeight - lastScrollHeight.current
          }
          lastScrollHeight.current = newScrollHeight
        }
      })
    },
    root: roomChatRef.current,
    threshold: 0.1
  })

  const handleNewMessageChange = (event) => {
    const value = event.target.value
    const messageLength = value.replace(/\n/g, '').length

    if (messageLength <= 100) {
      setNewMessage(value)
    }
  }

  const clearInput = () => {
    setNewMessage('')
    inputRef.current.value = null
  }

  const onSend = async () => {
    if (/^[\n\s]*$/.test(newMessage)) return
    try {
      clearInput()
      sendMessage(newMessage)
      await saveMessage(firstPage?.conversation_id, user.id, CHARACTER_NAVIGATOR_ID, newMessage)
      authDispatch({
        type: authActionTypes.SPEND_HEART,
        payload: 0
      })
    } catch {}
  }

  const characterLeft = useMemo(() => 100 - newMessage.length, [newMessage])

  return isNoContent ? (
    <div className="h-full flex items-center">
      <NoContent
        isModal
        message={'直前のページに戻ります。\nナビゲーターにお知らせください'}
        redirectUrl={MAIN_PATHS.NavigatorPage.replace(':characterId', 6)}
      />
    </div>
  ) : (
    <>
      <div className="relative h-[calc(100dvh-160px)]">
        <div className="h-[calc(100%-68px)] overflow-hidden hidden-scroll">
          <WrapperChatBox ref={roomChatRef}>
            {isFetching ? <LoadingIcon /> : <div id="start" />}
            {allMessages.map((message, i) => (
              <div key={i}>
                {message?.is_owner ? (
                  <ItemChatReceived data={message} className="!bg-[#B79EFF]" />
                ) : (
                  <ItemChatRealTalk
                    className="bg-[#F5EFFF]"
                    msg={message}
                    loveLvSettings={{}}
                    character={{}}
                    changeBgImage={() => {}}
                    isScrollOpenTalk={() => {}}
                    setMultiLevel={() => {}}
                    setIsMultiLoveModalOpen={() => {}}
                    handleOpenGift={() => {}}
                  />
                )}
                <br />
              </div>
            ))}
            <br />
          </WrapperChatBox>
        </div>
        <div className="bg-[#AF93FFE5]/80 py-3 px-4 w-full h-[68px]">
          <div className="flex items-end gap-x-2">
            <textarea
              id="input-message"
              ref={inputRef}
              value={newMessage}
              onChange={handleNewMessageChange}
              placeholder="メッセージを入力（最大100文字）"
              style={{
                width: 'calc(100% - 52px)',
                height: '2.25rem',
                maxHeight: 164,
                boxShadow: 'inset 0px 1px 3px rgba(0, 0, 0, 0.25)',
                minHeight: 36
              }}
              className="w-full h-full rounded-[3px] pl-3 py-2.5 placeholder-shown:text-xs placeholder-shown:text-[rgba(68,68,68,0.78)] placeholder-shown:font-light text-xs text-[#333333] font-light leading-[18px]"
              rows={1}
            />
            <div
              className="h-[2.25rem] w-[44px] bg-[#AF93FFE5] flex items-center justify-center rounded-[4px] cursor-pointer text-white"
              onClick={onSend}
            >
              送信
            </div>
          </div>
          <span className="text-white font-medium text-[13px] float-left">
            残り{characterLeft}文字
          </span>
        </div>
      </div>
    </>
  )
}

const WrapperChatBox = styled.div.attrs({
  className: 'h-full overflow-y-auto overflow-x-hidden hidden-scroll px-1'
})``

MessageNavigator.propTypes = {
  setUnreadNavigator: PropTypes.func
}
